import { displayDate } from '../../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { InputTableLayoutData } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { SellInvoiceLogModelType } from '@teinor/erp/types/company/order/sell/sellInvoice/sellInvoiceLog'
import { Fragment } from 'react'
import OrderStatusBar from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar'
import { getOrderStatusBarType } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar/functions'
import OrderStatusBarDropDown from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBarDropDown'
import { sellInvoiceStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { faPenCircle } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'


interface SellInvoiceStatusBarProps extends CreateStatusBarProps { 

}

const SellInvoiceStatusBar: React.FC<SellInvoiceStatusBarProps> = ({ completeData}) => {
    const { statuses } = useCreateStatusBar({ completeData })
    return (
        <OrderStatusBar statuses={statuses} currentOrderStatus={completeData?.header.status} getOrderStatusBarType={getOrderStatusBarType} isCanceled={completeData?.header?.status === sellInvoiceStatusInversed['refunded'] } />
    )
}

interface CreateStatusBarProps {
    completeData: InputTableLayoutData | null
}

const useCreateStatusBar = ({ completeData }: CreateStatusBarProps) => {
    const { t } = useTwinTranslation()
    const OrderLogs = completeData?.header?.OrderLogs
    const QuoteOrderId = completeData?.header?.QuoteOrderId
    const SellOrderId = completeData?.header?.SellOrderId
    const SellDeliveryNoteId = completeData?.header?.SellDeliveryNoteId
    let statuses: TwinDictionary[] = []
    const dictOrderLogs: TwinDictionary = {}
    for (const log of OrderLogs) {
        if (!dictOrderLogs[log.logType]) {
            dictOrderLogs[log.logType] = [log]
        } else {
            dictOrderLogs[log.logType] = [...dictOrderLogs[log.logType], log]
        }
    }
    if (SellDeliveryNoteId) {
        statuses.push({
            id: -1,
            name: t('deliveryNote', 'Albarán'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} SellDeliveryNoteId={SellDeliveryNoteId} /> : null
        })
    } else if (SellOrderId) {
        statuses.push({
            id: -1,
            name: t('order', 'Pedido'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} SellOrderId={SellOrderId} /> : null
        })
    } else if (QuoteOrderId) {
        statuses.push({
            id: -1,
            name: t('quoteOrder', 'Presupuesto'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} QuoteOrderId={QuoteOrderId} /> : null
        })
    } 
    statuses.push({
        id: 0,
        name: t('draft', 'Borrador'),
        logType: (SellDeliveryNoteId || SellOrderId || QuoteOrderId ? 1 : 0),
        color: 'bg-gray-33',
        icon: faPenCircle,
        className: 'text-gray-33',
        extraClassName: 'border border-gray-33',
        renderMessage: dictOrderLogs[(SellDeliveryNoteId || SellOrderId || QuoteOrderId ? 1 : 0)]?.length ? <RenderLogMessage logs={dictOrderLogs[(SellDeliveryNoteId || SellOrderId || QuoteOrderId ? 1 : 0)] || []} /> : null
    })
    statuses.push({
        id: 2,
        name: t('invoiced', 'Facturado'),
        logType: 2,
        color: 'bg-green-43',
        icon: faCheckCircle,
        className: 'text-green-43',
        renderMessage: dictOrderLogs[2]?.length ? <RenderLogMessage logs={dictOrderLogs[2] || []} /> : null
    })
    if (completeData?.header?.status === sellInvoiceStatusInversed['refunded']) {
        statuses.push({
            id: 4,
            name: t('refunded', 'Devuelto'),
            renderMessage: dictOrderLogs[4]?.length ? <RenderLogMessage logs={dictOrderLogs[4] || []} /> : null,
            logType: 4,
            color: 'bg-red-BA',
            icon: faXmarkCircle,
            className: 'text-red-BA',
        })
    }

    return { statuses }
}

interface RenderLogMessageProps {
    logs: SellInvoiceLogModelType[]
    QuoteOrderId?: number
    SellOrderId?: number
    SellDeliveryNoteId?: number
}

const RenderLogMessage: React.FC<RenderLogMessageProps> = ({ logs, QuoteOrderId, SellOrderId, SellDeliveryNoteId }) => {
    const { tVars } = useTwinTranslation()
    const renderMessage: JSX.Element[] = []
    for (const log of logs) {
        const myExtraData = log?.extraData ? JSON.parse(log?.extraData) : null
        const options = { employeeTrigger: log.EmployeeTrigger?.fullname_short || '', date: displayDate(new Date(log.createdAt)), full_assigned_number: myExtraData?.full_assigned_number || null, 'interpolation': { 'escapeValue': false }}
        const dictSellInvoiceLogsMessages: TwinDictionary = {
            0: SellDeliveryNoteId ?
                tVars('createdInvoiceBySellDeliveryNoteDateLogMessage', '{{employeeTrigger}} creó la factura el {{date}} a partir del albarán {{full_assigned_number}}', options) :
                SellOrderId ?
                tVars('createdInvoiceBySellOrderDateLogMessage', '{{employeeTrigger}} creó la factura el {{date}} a partir del pedido {{full_assigned_number}}', options) :
                QuoteOrderId ? 
                tVars('createdInvoiceByQuoteOrderDateLogMessage', '{{employeeTrigger}} creó la factura el {{date}} a partir del presupuesto {{full_assigned_number}}', options)
                : tVars('createdInvoiceLogMessage', '{{employeeTrigger}} creó la factura el {{date}}', options),
            1: '',
            2: tVars('finishInvoiceLogMessage', '{{employeeTrigger}} marcó la factura como finalizada el {{date}}', options),
            4: tVars('refundedInvoiceLogMessage', '{{employeeTrigger}} marcó la factura como devuelta el {{date}}', options),
        }
        if (dictSellInvoiceLogsMessages[log.logType]) {
            renderMessage.push(<div className='my-5' key={log.id} > {dictSellInvoiceLogsMessages[log.logType]} </div>)
        }
    }
    if (!renderMessage.length) {
        return null
    }
    return (<Fragment>{renderMessage}</Fragment>)
}

export const SellInvoiceStatusBarNew: React.FC<SellInvoiceStatusBarProps> = ({ completeData }) => {
    const { statuses } = useCreateStatusBar({ completeData })
    const sellInvoiceStatus = completeData?.header.status
    return (
        <OrderStatusBarDropDown statuses={statuses} currentOrderStatus={sellInvoiceStatus} getOrderStatusBarType={getOrderStatusBarType} isAccepted={sellInvoiceStatus === sellInvoiceStatusInversed['accepted']} isCanceled={sellInvoiceStatus === sellInvoiceStatusInversed['refunded']} />
    )
}

export default SellInvoiceStatusBar